import React from 'react';
import PropTypes, { number, shape, string } from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { MdLaunch } from 'react-icons/md';
import styled from 'styled-components/macro';

import { creditType, instanceType } from 'types';
import { baseSpacing, colors } from 'css/constants';
import logo from 'images/cvf-admin-logo-white.png';
import Nav from 'components/Nav';
import { CREDIT_STATES, UI } from 'utils/constants';
import ProjectSelectorBar from 'components/ProjectSelectorBar';
import { getNavigationPages } from './navigation';

const SidebarPrimitive = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  justify-content: space-between;
  width: ${UI.SIDEBAR_WIDTH};
  background-color: ${colors.darkGrey};
  color: #fff;
`;

const Logo = styled(Link)`
  display: block;
  margin: ${baseSpacing}px;

  img {
    width: 100%;
  }
`;

const UserGuide = styled.a`
  display: block;
  color: #fff;
  text-decoration: none;
  padding: ${baseSpacing}px;

  :hover,
  :active,
  :focus {
    text-decoration: underline;
  }

  svg {
    vertical-align: text-bottom;
  }
`;

const getPages = (projectId) => ({
  GROUPS: {
    id: 'groups',
    text: 'Groups',
    url: `/projects/${projectId}`,
    exact: false,
    isActive: (match, loc) => {
      if (match?.isExact) {
        return true;
      }

      return loc.pathname.includes('groups');
    }
  },
  INSTANCES: {
    id: 'instances',
    text: 'Flexboxes',
    url: `/projects/${projectId}/instances`,
    exact: false
  },
  USERS: {
    id: 'users',
    text: 'Users',
    url: `/projects/${projectId}/users`
  },
  SESSIONS: {
    id: 'sessions',
    text: 'Sessions',
    url: `/projects/${projectId}/sessions`
  },
  CREDITS: {
    id: 'credits',
    text: 'Credits',
    url: `/projects/${projectId}/credits`
  },
  PAYMENT_OPTIONS: {
    id: 'paymentOptions',
    text: 'Payment Options',
    url: `/projects/${projectId}/paymentOptions`
  },
  GUIDE: {
    id: 'guide',
    text: 'Flexbox Setup Guide',
    url: `/projects/${projectId}/guide`,
    exact: false
  }
});

const Sidebar = ({ projectId, projects, credits, instances, isAdmin }) => {
  const location = useLocation();
  const pages = getPages(projectId);
  const logoPathname = projectId ? `/projects/${projectId}` : '/';

  const hasCredits =
    credits.filter(({ state }) =>
      [
        CREDIT_STATES.ACTIVE,
        CREDIT_STATES.INACTIVE,
        CREDIT_STATES.SCHEDULED
      ].includes(state)
    ).length > 0;
  const hasPAYGFlexbox = instances.some(
    ({ project_instance_data }) =>
      project_instance_data?.plan?.is_contract === false
  );
  const displayContractPages = hasCredits || hasPAYGFlexbox;

  const adminPages = [
    pages.GROUPS,
    pages.INSTANCES,
    pages.USERS,
    pages.SESSIONS,
    ...(displayContractPages ? [pages.CREDITS, pages.PAYMENT_OPTIONS] : []),
    pages.GUIDE
  ];

  const groupCoordinatorPages = [pages.GROUPS];

  return (
    <>
      {projects && projects.length > 1 && <ProjectSelectorBar />}
      <SidebarPrimitive>
        <div>
          <Logo
            to={{
              pathname: logoPathname,
              search: location.search
            }}
          >
            <img src={logo} alt="ClearView Flex Admin Logo" />
          </Logo>

          <Nav
            items={getNavigationPages(
              projectId,
              isAdmin,
              adminPages,
              groupCoordinatorPages
            )}
            location={location}
          />
        </div>

        <UserGuide
          href="https://help.sohonet.com/en/"
          rel="noopener noreferrer"
          target="_blank"
          title="Open CVF Help Center in new tab"
        >
          Help Center <MdLaunch />
        </UserGuide>
      </SidebarPrimitive>
    </>
  );
};

Sidebar.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  projects: PropTypes.arrayOf(
    shape({
      id: number.isRequired,
      name: string.isRequired
    })
  ),
  credits: PropTypes.arrayOf(creditType),
  instances: PropTypes.arrayOf(instanceType),
  isAdmin: PropTypes.bool
};

export default Sidebar;
